import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { create as createFedopsLogger } from '@wix/fedops-logger';
import i18n from './i18n';
import App from './components/App';
import { ErrorDataProvider } from './hooks/use-error-data';
import { BrandStyle } from './components/constants';
import { STANDALONE_CODES } from './components/constants/custom-pages';
import { SYMPHONY_FAVICON_HREF } from './components/constants/symphony-favicon';

const locale = window.__LOCALE__;
const errorData = window.__ERROR_DATA__;
const isNotBranded = errorData.errorCode?.endsWith('-NotBranded');
const standaloneBaseCode = errorData.errorCode?.replace('-NotBranded', '');
const isStandalone =
  !!standaloneBaseCode && STANDALONE_CODES.includes(standaloneBaseCode);
const isWixel = errorData.brand === BrandStyle.wixel;
const isBase44 = errorData.brand === BrandStyle.base44;
const isSymphony = errorData.brand === BrandStyle.symphony;
const brandStyle = isWixel
  ? BrandStyle.wixel
  : isBase44
  ? BrandStyle.base44
  : isSymphony
  ? BrandStyle.symphony
  : isNotBranded
  ? BrandStyle.notBranded
  : BrandStyle.classic;

const fedopsLogger = createFedopsLogger('classic-error-pages-statics');

// Move the following `appLoaded()` call to the point where your app has fully loaded.
// See https://github.com/wix-private/fed-infra/blob/master/fedops/fedops-logger/README.md
fedopsLogger.appLoaded();

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

const supportedLanguages = [
  'en',
  'cs',
  'da',
  'de',
  'es',
  'fr',
  'id',
  'it',
  'ja',
  'ko',
  'nl',
  'no',
  'pl',
  'pt',
  'ru',
  'sv',
  'th',
  'tr',
  'uk',
  'vi',
  'zh',
];

const render = () =>
  root.render(
    <Suspense>
      <I18nextProvider i18n={i18n(locale)}>
        <ErrorDataProvider errorData={errorData}>
          <App brandStyle={brandStyle} />
        </ErrorDataProvider>
      </I18nextProvider>
    </Suspense>,
  );
const isFreeSite = window.location.origin.indexOf('wixsite.com') >= 0;
const initHeaderFooter = (brand: string) =>
  window['header-footer-provider']
    .initHeaderFooter({
      footer: false,
      languagePickerList: supportedLanguages,
      header: true,
      language: locale,
      hiddenLinks: isFreeSite ? ['language_picker', 'log_in'] : [],
      disableAuthCheck: isFreeSite,
      brand,
    })
    .then(render)
    .catch(render);
// The header-footer service has no Symphony entry, so it answers with empty
// content; the brand ships its own header as a standalone bundle instead.
const SYMPHONY_HEADER_URL =
  'https://static.parastorage.com/services/symphony-header/1.8.0/';

const renderSymphonyHeader = () => {
  const stylesheet = document.createElement('link');
  stylesheet.rel = 'stylesheet';
  stylesheet.href = `${SYMPHONY_HEADER_URL}app.min.css`;
  document.head.appendChild(stylesheet);

  const target = document.createElement('div');
  document.body.insertBefore(target, document.body.firstChild);

  const script = document.createElement('script');
  script.src = `${SYMPHONY_HEADER_URL}app.bundle.min.js`;
  script.onload = () => window.SymphonyHeader?.render({ target });
  document.body.appendChild(script);
};

const applySymphonyFavicon = () => {
  document
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]')
    .forEach((link) => link.remove());

  const icon = document.createElement('link');
  icon.rel = 'icon';
  icon.type = 'image/svg+xml';
  icon.href = SYMPHONY_FAVICON_HREF;
  document.head.appendChild(icon);
};

if (isSymphony) {
  render();
  renderSymphonyHeader();
  applySymphonyFavicon();
} else if (isNotBranded || isStandalone) {
  render();
} else {
  initHeaderFooter(isWixel ? 'wixel' : isBase44 ? 'base44' : 'wix');
}
